import { HttpClient, HttpContext } from '@angular/common/http'
import { TranslationObject } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { Observable, defer, throwError, timer } from 'rxjs'
import { catchError, finalize, retry } from 'rxjs/operators'

import { BYPASS_LOADER } from '@@services/http-context-tokens'

/** Document event announced when a language's words are missing; AppComponent listens for it. */
export const TRANSLATION_LOAD_FAILED_EVENT = 'translation-load-failed'

const TRANSLATION_LOAD_RETRIES = 2
const TRANSLATION_RETRY_DELAY_MS = 1000

/** Tells AppComponent that <lang>'s words are missing, so it can offer a refresh (NB-5629). */
export function announceTranslationLoadFailure(lang: string): void {
    document.dispatchEvent(
        new CustomEvent(TRANSLATION_LOAD_FAILED_EVENT, {
            detail: { data: { lang } }
        })
    )
}

/**
 * NB-5629: downloads the language file exactly like TranslateHttpLoader, but retries a failed download
 * before ngx-translate sees it. Once the library records a failure it keeps no dictionary, caches the
 * failed request and never asks again, so every label shows the last word of its key until a full reload.
 * When every try fails, the failure is announced (so AppComponent can offer a refresh) and re-thrown as before.
 */
export class RetryingTranslateLoader extends TranslateHttpLoader {
    // Downloads in flight per language, retries included; a printout can fetch the page's language at the same time
    private static readonly downloadsInFlight = new Map<string, number>()

    /** True while <lang>'s file is still downloading, so a word missing meanwhile is not a failure yet. */
    static isDownloading(lang: string): boolean {
        return (RetryingTranslateLoader.downloadsInFlight.get(lang) ?? 0) > 0
    }

    private static countDownload(lang: string, change: 1 | -1): void {
        const inFlight =
            (RetryingTranslateLoader.downloadsInFlight.get(lang) ?? 0) + change
        if (inFlight > 0) {
            RetryingTranslateLoader.downloadsInFlight.set(lang, inFlight)
        } else {
            RetryingTranslateLoader.downloadsInFlight.delete(lang)
        }
    }

    constructor(
        private readonly httpClient: HttpClient,
        prefix: string,
        suffix: string
    ) {
        super(httpClient, prefix, suffix)
    }

    override getTranslation(lang: string): Observable<TranslationObject> {
        // Off the page spinner: retries would flicker it, and a stuck spinner would cover the refresh prompt
        const context = new HttpContext().set(BYPASS_LOADER, true)
        const download = this.httpClient
            .get<TranslationObject>(`${this.prefix}${lang}${this.suffix}`, {
                context
            })
            .pipe(
                // Unlike RestClientService's status-0-only retry: any failure of a static file is worth another try
                retry({
                    count: TRANSLATION_LOAD_RETRIES,
                    delay: (_error, retryCount) =>
                        timer(retryCount * TRANSLATION_RETRY_DELAY_MS)
                }),
                catchError((error) => {
                    announceTranslationLoadFailure(lang)
                    return throwError(() => error)
                })
            )
        // Counted from the first try until the last one ends, so a word missing meanwhile isn't reported as a failure
        return defer(() => {
            RetryingTranslateLoader.countDownload(lang, 1)
            return download
        }).pipe(finalize(() => RetryingTranslateLoader.countDownload(lang, -1)))
    }
}
